<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import FormRow from "@/components/Common/FormRow.vue"
import PermissionItem from "@/components/UserRoleForm/PermissionItem.vue";
import UserRoleService from "@/services/models/UserRole";
import SelectRoleChecker from '@/router/selectRoleChecker'
import permissionType from "@/types/permission"
import getPermissionListResponseType from '@/types/responses/getPermissionList'
import getRoleTemplateResponseType from '@/types/responses/getRoleTemplate'
import getPermissionListRequestType from '@/types/requests/getPermissionList'
import {officeType, departmentType} from "@/types/requests/userRoleSearchForm";
import Api from "@/services/api/ApiServiceFabrick";
import { permissionDisplayName } from "@/services/consts";
import {
  MANAGEMENT_OFFICE,
  SUB_MANAGEMENT_OFFICE,
  INSTALLATION_OFFICE,
  OPERATION_OFFICE,
  DEPARTMENT
} from "@/services/consts/user_role";
import i18n from '@/lang/i18n';

const permissions = ref({} as getPermissionListResponseType)
const permissionList = ref({} as getPermissionListResponseType)
const selectRoleTemplateItems = ref([] as string[]);
const selectedItem = ref("" as string)
const isPermissionListShow = ref(false)
const maskText = ref(i18n.global.t('pages.userRoles.form.mask.needSelect'))
const props = defineProps<{
  windowInnerWidth: number
  modelValue: permissionType[]
  roleTemplateList: getRoleTemplateResponseType[]
  selectedOffice: officeType
  selectedDepartment: departmentType
  errors: any
  isEdit: boolean
}>()
const emits = defineEmits([
  'update:modelValue',
  'changeBtnStatus'
])

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits("update:modelValue", value);
  },
})

watch(() => permissions, () => {
  formatedPermission()
},{deep:true})

watch(() => props.selectedOffice, () => {
  initPermission()
})
watch(() => props.selectedDepartment, () => {
  initPermission()
})
watch(() => props.isEdit, () => {
  initPermission()
})

function getPermissionList(){
  const param = makeParam()
  Api().getPermissionList(param).then((data: getPermissionListResponseType) => {
    permissionList.value = data
    setPermission()
    isPermissionListShow.value = true
    emits('changeBtnStatus', true);
  }).catch((error: any) => {
    return console.log(error.response)
  })
}
function makeParam(){
  let selectedOrganizationType = "";
  let selectedOrganization = {} as {id: number|null, name: string, type?: string, operation_office_id?: number|null}
  if (!props.selectedDepartment || !props.selectedDepartment.id) {
    selectedOrganizationType = getSelectOrganizationType()
    selectedOrganization = props.selectedOffice
  } else {
    selectedOrganizationType = DEPARTMENT.type
    selectedOrganization = props.selectedDepartment
  }

  const param:getPermissionListRequestType =
  {
    id: selectedOrganization.id,
    type: selectedOrganizationType,
    name: selectedOrganization.name,
  }
  return param
}
function getSelectOrganizationType(){
  const organizations = [
    MANAGEMENT_OFFICE,
    SUB_MANAGEMENT_OFFICE,
    INSTALLATION_OFFICE,
    OPERATION_OFFICE,
    DEPARTMENT
  ]
  const organization = organizations.find( (org) => {
    return org.type.includes(props.selectedOffice.type)
  })
  return organization.type
}

function initPermission(){
  isPermissionListShow.value = false
  maskText.value = i18n.global.t('pages.userRoles.form.mask.needSelect')
  emits('changeBtnStatus', false);
  if(!props.isEdit) permissions.value = {}
  permissionList.value = {}
  selectedItem.value = ""

  let isDepartment = false;
  const current_role = SelectRoleChecker.getCurrentRole()
  if(current_role.organization_type == DEPARTMENT.type) isDepartment = true

  if (
    ( props.isEdit && Object.keys(modelValue.value).length === 0 ) ||
    ( Object.keys(props.roleTemplateList).length === 0 ) ||
    ( !props.selectedOffice || !props.selectedOffice.id ) ||
    ( isDepartment && (!props.selectedDepartment || !props.selectedDepartment.id) )
  ) {
    return delayInitPermission()
  }

  // display permission list
  maskText.value = i18n.global.t('pages.userRoles.form.mask.loading')
  selectRoleTemplateItems.value = getRoleTemplateNames(props.roleTemplateList)
  getPermissionList()
}

function delayInitPermission(){
  setTimeout(() => {initPermission()},1000)
}

function setPermission(){
  if(!modelValue.value) return
  permissions.value = UserRoleService.formatPermissionsToForm(modelValue.value);
}

function formatedPermission(){
  modelValue.value = UserRoleService.formatPermissionsToRequest(permissions.value);
}

function setRoleTemplate(){
  const selectedTemplate = props.roleTemplateList.find( (template) =>{
    return template.name == selectedItem.value
  })
  if(selectedTemplate) {
    const formatedPermissions = UserRoleService.formatPermissionsToForm(selectedTemplate.permissions);
    permissions.value = JSON.parse(JSON.stringify(formatedPermissions))
  }
}

function getRoleTemplateNames(data: getRoleTemplateResponseType[]){
  let templateNameList = [] as string[]
  let rejectedType = [] as string[]
  const selectedOrganizationType = getSelectOrganizationType()
  switch(selectedOrganizationType){
    case MANAGEMENT_OFFICE.type:
      break;
    case SUB_MANAGEMENT_OFFICE.type:
      rejectedType = [
        MANAGEMENT_OFFICE.type
      ]
      break;
    case INSTALLATION_OFFICE.type:
      rejectedType = [
        MANAGEMENT_OFFICE.type,
        SUB_MANAGEMENT_OFFICE.type,
        OPERATION_OFFICE.type,
        DEPARTMENT.type
      ]
      break;
    case OPERATION_OFFICE.type:
      rejectedType = [
        MANAGEMENT_OFFICE.type,
        SUB_MANAGEMENT_OFFICE.type,
        INSTALLATION_OFFICE.type
      ]
      break;
    case DEPARTMENT.type:
      rejectedType = [
        MANAGEMENT_OFFICE.type,
        SUB_MANAGEMENT_OFFICE.type,
        INSTALLATION_OFFICE.type
      ]
      break;
  }

  data.forEach((permission) => {
    if(rejectedType.includes(permission.organization_type)) return
    templateNameList.push(permission.name)
  })
  return templateNameList
}

</script>
<template>
  <FormRow
    :title="i18n.global.t('pages.userRoles.form.template')"
    :windowInnerWidth="props.windowInnerWidth"
    :required="false"
    v-if="isPermissionListShow"
  >
    <v-row>
      <v-col cols="6">
        <v-select
          :label="i18n.global.t('pages.userRoles.form.selectTemplate')"
          v-model="selectedItem"
          :items="selectRoleTemplateItems"
          :clearable="true"
        />
      </v-col>
      <v-col cols="2">
        <v-btn
          @click="setRoleTemplate"
        >
          {{ i18n.global.t('pages.userRoles.form.button.apply') }}
        </v-btn>
      </v-col>
    </v-row>
  </FormRow>
  <FormRow
    :title="i18n.global.t('pages.userRoles.form.permission')"
    :windowInnerWidth="props.windowInnerWidth"
    :required="true"
  >
    <v-row v-if="errors">
      <template v-for="(item) in errors" :key="item">
        <div class="ml-3 mt-6 text-error">{{ item }}</div>
      </template>
    </v-row>

    <v-row>
      <v-col cols="10" v-if="isPermissionListShow">
        <template
          v-for="(actions, permissionName) in permissionList"
            :key="permissionName"
        >
          <PermissionItem
            :label="permissionDisplayName[permissionName]"
            :permissionName="String(permissionName)"
            v-model="permissions[permissionName]"
            :actionList="actions"
          />
        </template>
      </v-col>
      <v-col cols="12" v-else>
        <div class="permission-mask d-flex justify-content-center align-items-center">
          <div class="permission-mask-label">
            {{ maskText }}
          </div>
        </div>
      </v-col>
    </v-row>

  </FormRow>

</template>
<style scooped>
div.permission-mask{
  height: 60px;
  z-index: 1000;
  background-color: rgba(80, 80, 80, 0.5);
}
.permission-mask-label{
  color: white;
  font-weight: bold;
}
</style>